import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, Collapse, makeStyles } from '@material-ui/core';
import ActionButton from '../../../actionButton';
import { ACTION_BUTTON_TYPES } from '../../../actionButton/config';
import { getColumnWidth } from '../../../../../constants/gridTable';
import { isArrayEmpty } from '../../../../../utility/helpers';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    backgroundColor: primary.bluish9,
    borderRadius: 8,
    boxSizing: 'border-box',
    cursor: 'pointer',
    width: '100%',
    transition: 'background-color .3s ease',
    '&:hover': {
      backgroundColor: ({ isExpanded }) =>
        isExpanded ? primary.bluish9 : primary.bluish8,
    },
  },
  info: {
    display: 'grid',
    alignItems: 'center',
    height: 68,
    width: '100%',
  },
  cell: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    paddingRight: spacing(2),
  },
  firstCellValue: {
    maxWidth: 'calc(100% - 60px)',
  },
  actionCellValue: {
    width: '100%',
  },
  highlighted: {
    fontFamily: 'ProximaNova-Bold',
  },
  expandButtonWrapper: {
    flexShrink: 0,
  },
  expandButton: {
    flexShrink: 0,
    marginRight: spacing(4),
    marginLeft: spacing(3.5),
  },
  expandIcon: {
    transform: 'rotate(0deg)',
    transition: 'transform .3s cubic-bezier(0.77, 0.2, 0.05, 1)',
  },
  expandIconOpened: {
    transform: 'rotate(180deg)',
  },
}));

const Row = ({
  translations,
  columns,
  rowData,
  detailsComponent: DetailsComponent,
  detailsComponentProps = {},
  onGetRowDetails,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [rowDetails, setRowDetails] = useState([]);

  const classes = useStyles({ isExpanded });

  const handleGetRowDetails = useCallback(async () => {
    if (isExpanded) {
      setIsExpanded(false);
    } else {
      const { data } = await onGetRowDetails(rowData.id);
      setRowDetails(data);
      setIsExpanded(true);
    }
  }, [rowData, isExpanded, onGetRowDetails]);

  return (
    <div className={classes.root} onClick={handleGetRowDetails}>
      <div className={classes.info} style={getColumnWidth(columns)}>
        {columns.map((column, index) => (
          <div
            key={`row_cell_${rowData.id}_${column.id}_${index}`}
            className={classes.cell}
          >
            {index === 0 && (
              <ActionButton
                rootClass={classes.expandButtonWrapper}
                className={classes.expandButton}
                type={ACTION_BUTTON_TYPES.ARROW}
                iconClass={classNames(classes.expandIcon, {
                  [classes.expandIconOpened]: isExpanded,
                })}
                tooltipText={translations.expandTooltipLabel}
              />
            )}
            <Typography
              className={classNames({
                [classes.highlighted]: column.isHighlighted,
                [classes.firstCellValue]: index === 0,
                [classes.actionCellValue]: column.isHeaderAction,
              })}
              variant="body1"
              component="div"
            >
              {rowData[column.rowKey]}
            </Typography>
          </div>
        ))}
      </div>
      <Collapse in={isExpanded && !isArrayEmpty(rowDetails)} unmountOnExit>
        <DetailsComponent
          translations={translations}
          data={rowDetails}
          {...detailsComponentProps}
        />
      </Collapse>
    </div>
  );
};

Row.propTypes = {
  translations: PropTypes.object.isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  rowData: PropTypes.object.isRequired,
  detailsComponent: PropTypes.elementType.isRequired,
  detailsComponentProps: PropTypes.object,
  onGetRowDetails: PropTypes.func.isRequired,
};

export default Row;
